import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { EntryWidgetProps } from './types';

/*
 * Frequency counter — one scalar (count). The server rejects valueJson
 * for frequency targets, so this widget never sends it.
 *
 * UX: +/- buttons with a numeric input in the middle. Tapping + on a
 * null state initializes to 1; tapping – on 0 stays at 0 (no negative
 * counts).
 */
export function FrequencyCounter({ value, onChange, disabled }: EntryWidgetProps) {
    const count = value.valueNumeric ?? 0;

    const setCount = (n: number) => {
        const safe = Math.max(0, Math.floor(n));
        onChange({ ...value, valueNumeric: safe, valueJson: null, dirty: true });
    };

    return (
        <div className="flex items-center gap-2">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled || count <= 0}
                onClick={() => setCount(count - 1)}
                aria-label="Decrement count"
            >
                <Minus className="w-3.5 h-3.5" />
            </Button>
            <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={value.valueNumeric ?? ''}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                        onChange({ ...value, valueNumeric: null, valueJson: null, dirty: true });
                        return;
                    }
                    const n = Number(raw);
                    if (Number.isFinite(n)) setCount(n);
                }}
                disabled={disabled}
                className="w-20 px-2 py-1 text-center text-lg font-mono bg-background border border-border rounded-md focus:border-accent focus:outline-none disabled:opacity-50"
            />
            <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => setCount(count + 1)}
                aria-label="Increment count"
            >
                <Plus className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs text-text-subtle ml-1">occurrences</span>
        </div>
    );
}
